var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ABTestNameEnum, ABTestVariationEnum, ThemeBreakpointIndexEnum } from '@inter-ikea-kompis/enums';
import { ThemeBreakpointObserver } from '@inter-ikea-kompis/themes';
import { DexfSettingsUtility, ProductUtility, SafariWriteDirectionUtility, UrlUtility } from '@inter-ikea-kompis/utilities';
import { AddToBagStateEnum, UnavailableOnlineBehaviourEnum } from '@inter-ikea-kompis/component-add-to-bag';
import { AssemblyServices } from '@inter-ikea-kompis/component-assembly-services';
import { AssistanceCard } from '@inter-ikea-kompis/component-assistance-card';
import { ConfigurationSummary, ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum, ConfigurationSummaryConfirmationCardStateEnum, ConfirmationSummaryKioskMainCTAButtonStateEnum, ConfirmationSummaryShareDesignStateEnum } from '@inter-ikea-kompis/component-configuration-summary';
import { Image } from '@inter-ikea-kompis/component-image';
import { MediaCarousel } from '@inter-ikea-kompis/component-media-carousel';
import { ProductRowProductLinkEnum, ProductRowProductMeasureEnum } from '@inter-ikea-kompis/component-product-row';
import { QrCode } from '@inter-ikea-kompis/component-qr-code';
import { Text } from '@inter-ikea-kompis/component-text';
import { ZipIn, ZipInCardStateEnum, ZipInModalEnum } from '@inter-ikea-kompis/component-zip-in';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { checkmarkCircle, incorrect, informationCircle, pencil, printer, registerIcon, warningTriangle } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import { Tabs as SkapaTabs } from '@ingka/tabs-webc/passive.js';
import { DesignSourceEnum } from '@insights/insights-data-provider';
import InlineMessageIconEnum from '../../enums/InlineMessageIconEnum.js';
import InlineMessageLayoutEnum from '../../enums/InlineMessageLayoutEnum.js';
import SummaryPageConfigurationMeasureEnum from '../../enums/SummaryPageConfigurationMeasureEnum.js';
import SummaryPageModalEnum from '../../enums/SummaryPageModalEnum.js';
import SummaryPageTabEnum from '../../enums/SummaryPageTabEnum.js';
import SummaryPageAnalyticsContext from '../../trackers/SummaryPageAnalyticsContext.js';
import SummaryPageTracker from '../../trackers/SummaryPageTracker.js';
import SummaryPageBrowserUtility from '../../utilities/SummaryPageBrowserUtility.js';
import SummaryPageCustomTabsUtility from '../../utilities/SummaryPageCustomTabsUtility.js';
import SummaryPageMeasurementUtility from '../../utilities/SummaryPageMeasurementUtility.js';
import SummaryPageImages from '../summary-page-images/SummaryPageImages.js';
import SummaryPagePrint from '../summary-page-print/SummaryPagePrint.js';
import SummaryPageProductList from '../summary-page-product-list/SummaryPageProductList.js';
import SummaryPageSecurity from '../summary-page-security/SummaryPageSecurity.js';
import SummaryPageTabs from '../summary-page-tabs/SummaryPageTabs.js';
import SummaryPageStyle from './SummaryPageStyle.js';
/**
 * The Summary Page component is used as the end step in a typical planning journey. Its purpose is to summarise the design with the included products, image(s), and other relevant information in order to make the user confident to add their design to the shopping bag.
 *
 * @event storeSelectorChange Triggered when the selected store changes.
 * @event sortListChange Triggered when the selected way of sorting changes.
 * @event addToCart When add to cart is clicked.
 * @event addToList When add to shopping list is clicked.
 * @event addToCartClick User clicks on add to cart button.
 * @event shareDesign When share design is clicked.
 * @event saveDesign When save design is clicked.
 * @event logInAndSaveDesign When login is clicked inside the saveDesignToProfile prompt.
 * @event modalOpen Triggered when a sheet is opened.
 * @event modalClose Triggered when the user closes a sheet.
 * @event tabChange Triggered when the selected tab changes.
 * @event selectedImageIndexChange Triggered when the selected image changes in the "Images" tab.
 * @event copyDesign When copy design is clicked.
 * @event addUnavailableProductsToListChange Changes the option "addUnavailableProductsToList". Triggered by user clicking the checkbox.
 * @event addUnavailableToCartAndList Start adding unavailable products to shopping cart and shopping list.
 * @event unavailableProductsModalContinue Click on continue in the unavailable products modal.
 * @event collectProduct Triggered when a product is collected.
 * @event shareDesignCardStateChange Triggered when the state for ShareDesignCard changes.
 * @event sendByEmailInput Triggered when the user writes in the sendByEmail view.
 * @event sendByEmailValidation Triggered when the user tries to send with an invalid email.
 * @event sendByEmailSend Triggered when the user clicks send in the sendByEmail view.
 * @event sendByEmailReset Triggered 3 seconds after SendByEmailState is set to success.
 * @event copyDesignCodeClick Triggered when the user clicks the copy design code button.
 * @event copyDesignCodeReset Triggered 3 seconds after copyDesignCodeState is set to success.
 * @event copyDesignLinkClick Triggered when the user clicks the copy design link button.
 * @event copyDesignLinkReset Triggered 3 seconds after copyDesignLinkState is set to success.
 * @event forgetZipCode User clicks on the forget link from Home Delivery.
 * @event saveZipCode User clicks on the save link from Home Delivery.
 * @event openZipIn User requests to select zip code.
 * @event confirmationCardStateChange Triggered when the state for ConfirmationCard in ConfigurationSummary changes.
 * @event financialServicesContinueButtonClick Triggered when continue planning button is clicked in financial services sheet.
 * @event financialServicesMoreInfoClick Triggered when read more is clicked in financial services sheet.
 * @event resetAddToCartState Sent after 3 seconds after addToCartState confirmation to be able to reset the state of the button.
 * @event resetAddToListState Sent after 3 seconds after addToListState confirmation to be able to reset the state of the button.
 * @event productListAdd Triggered when the add button is clicked on a product in product list.
 * @event productListRemove Triggered when the remove button is clicked on a product in product list.
 * @event kioskMainCtaButtonClick Triggered when main call to action button on kiosk is clicked.
 * @event productListExpand Triggered when included shopping products in product list gets expanded.
 * @event linkCardClick When the link card is clicked.
 * @event printButtonClick When the print button is clicked.
 * @event editDesignButtonClick When the edit design button is clicked.
 * @event printQrCodeRendered Triggered when the print QR code has rendered.
 * @event productListNameClick Triggered when the name of a product in the product list is clicked.
 * @event assistanceCardLinkClick Triggered when the link of the assistance card is clicked.
 *
 * @slot custom-print-content - Slot for custom content to be included in the print layout.
 * @slot custom-tab-1 - Slot for custom tab content.
 * @slot custom-tab-2 - Slot for custom tab content.
 * @slot custom-tab-3 - Slot for custom tab content.
 * @slot custom-tab-4 - Slot for custom tab content.
 * @slot custom-tab-5 - Slot for custom tab content.
 *
 * @tracker packages/component-summary-page/src/trackers/SummaryPageTracker.ts
 */
export default class SummaryPage extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // A list of store availability objects.
        this.storeAvailabilities = null;
        // A list of stores, when left empty the store selector will be hidden
        this.stores = null;
        // The storeId of the selected store
        this.storeId = null;
        // Controls how measurements should be shown in the Product list tab
        this.productMeasures = ProductRowProductMeasureEnum.none;
        // Controls how measurements should be shown in the configuration summary.
        this.configurationMeasures = SummaryPageConfigurationMeasureEnum.short;
        // The url for the screenshot.
        this.screenshotUrl = null;
        // The alt text for the screenshot. If not set, a default alt text will be used.
        this.screenshotAlt = null;
        // The name of the product, e.g. PLATSA
        this.productName = null;
        // Description of the product, e.g. "Wardrobe, 200x300x55 cm". Used in SaveDesignToProfile and ShareDesignCard.
        this.productDescription = null;
        // Shows the share design button.
        this.showShareDesign = false;
        // Shows the save design button.
        this.showSaveDesign = false;
        // Shows the add to bag component. This attribute should be set to "false" when in a Kiosk. Setting this to "false" also moves the share button from the priceHeader down to the text buttons.
        this.showAddToBag = false;
        // VPC code for the solution.
        this.vpcCode = null;
        // Disables outgoing links. Should be true for kiosks.
        this.disableLinks = false;
        // The currently visible sheet.
        this.visibleModal = null;
        // Defines the state for add to bag button
        this.addToCartState = AddToBagStateEnum.default;
        // Defines the state for add to list button
        this.addToListState = AddToBagStateEnum.default;
        // Defines the state for the share design button.
        this.shareDesignState = ConfirmationSummaryShareDesignStateEnum.default;
        // Defines the state for the buy in store button (showKioskMainCTAButton).
        this.kioskMainCTAButtonState = ConfirmationSummaryKioskMainCTAButtonStateEnum.default;
        // Defines the state for the confirmation card in configuration summary
        this.confirmationCardState = ConfigurationSummaryConfirmationCardStateEnum.default;
        // Type of sheet to show for add to cart, shopping list buttons and when clicking the save design button fails.
        this.confirmationCardType = null;
        // A list of images used in the "Images" tab
        this.images = null;
        // An object with information for the security tab
        this.securityInformation = null;
        // Measurements of height, width and depth in millimeter. Can be left empty to hide dimensions.
        this.measurements = null;
        // Value for checkbox to add unavailable items to shopping list when they are not available to be added to the cart.
        this.addUnavailableProductsToList = false;
        // Used as a link for the QR code and in ShareDesignCard. When copied, query parameter with vpc source is added.
        this.designLink = null;
        // A collection of props used for the sheet shown after clicking share design. For more info about the props check the ShareDesignCard component.
        this.shareDesignCard = null;
        // A collection of props used for the sheet shown after clicking buy in store. For more info about the props check the BuyInStore component.
        this.buyInStore = null;
        // A collection of props used for the sheet shown after clicking save design. For more info about the props check the SaveDesignToProfile component.
        this.saveDesignToProfile = null;
        // Defines the state for the log in and save button.
        this.logInAndSaveState = ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum.default;
        // A collection of props used for the home delivery availability in product list. For more info about the props check the HomeDelivery component.
        this.homeDelivery = null;
        // A collection of props used for the sheet shown after clicking assembly services. For more info about the props check the AssemblyServices component.
        this.assemblyServices = null;
        // Zip code will be passed down to assembly services and home delivery.
        this.zipCode = null;
        // State passed to zip in.
        this.zipInCardState = ZipInCardStateEnum.default;
        // Whether to show the collect checkbox in the product list or not.
        this.collectableProducts = false;
        // A list of product ids that are collected.
        this.collectedProducts = null;
        // Show text describing the color and/or material of the product.
        this.showDesignText = false;
        // A list of sections used in the product list.
        this.productListSections = null;
        // Defines optional custom tab buttons. Up to five tabs are supported.
        this.customTabButtons = null;
        // List of failed shopping products returned when trying to add to cart.
        this.failedShoppingItems = null;
        // Highest heading on the Summary page will be h2 as opposed to h1. Set to true if planner is e.g. used in an iframe with an h1 header outside of the planner.
        this.startHeadingsAtH2 = false;
        // Shows financial services. The financing option is retrieved by service.
        this.financingOption = null;
        // Deprecated. Adds balanced margins on all sides of SummaryPage when true. To be used with the summary header and footer. This prop is deprecated - use the Main Header and Main Footer components together with the enableMainMargins prop instead.
        this.enableMargins = false;
        // Adds margins on all sides of SummaryPage when true. To be used with the main header and footer.
        this.enableMainMargins = false;
        // Shows total package weight of the shopping products.
        this.showTotalPackageWeight = false;
        // Shows the assistance card, which informs the user how to get assistance with their design. It has a kiosk state with a different look when the prop disableLinks is set to true.
        this.showAssistanceCard = false;
        // Shows the assembly services card, which presents the assembly service availability to the user.
        this.showAssemblyServices = false;
        // The name of the application, e.g. "pax". Used in the booking url in the assistance card.
        this.assistanceCardApplicationName = null;
        // Shows call to action button to give user the possibility to complete their purchase on kiosk. Won't be shown if showAddToBag is true.
        this.showKioskMainCTAButton = false;
        // Shows print button. For example to be used on kiosks with print functionality.
        this.showPrintButton = false;
        // Shows edit design button.
        this.showEditDesignButton = false;
        // Used for automatic analytics. If null or not set, analytics is turned off.
        this.analytics = null;
        // A list of included products within SPR products. Must be set when using layout compact print and printing SPR's. Must also be set (if applicable) for total package weight.
        this.includedProducts = null;
        // A list of product IDs that can be added in the product list.
        this.productListAddable = null;
        // A list of product item numbers that can be removed in the product list.
        this.productListRemovable = null;
        // Sets the default sorting of the product list.
        this.defaultSortBy = null;
        // Inline messages, displayed below ConfigurationSummary.
        this.messageSection = null;
        // Displays link card when data and Översätta key showLinkCard are set. For image ratio guidelines, see Skapa Card. Hidden on kiosks when disableLinks is true.
        this.linkCard = null;
        // A sorted list of content to include in the print layout. When not set, the currently shown tab is printed. To include custom content in the print, the content must also be included as the slot "custom-print-content". E.g. ['productListTab', 'imagesTab', 'securityTab', 'customPrintContent'].
        this.contentToPrint = null;
        // Layout for summary page. NOTE: ProductRowLayoutEnum.compactPrint should only be used during print. Make sure SPR's has their child articles in includedProducts, since SPR's are not visible in compactPrint.
        this.pickingListPrintLayout = null;
        // Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
        this.measurementSystem = null;
        // If set, the Summary Page component will hoist itself to this element during print. Should probably be set to `document.body`. Make sure to hide all other UI during print.
        this.printHoistTarget = null;
        // Recommended pattern for normal and kiosk mode, is eventOnly to open a ProductInformationCard in a Skapa sheet modal. If using productInformationPage, disable on kiosk.
        this.productListLink = ProductRowProductLinkEnum.productInformationPage;
        // Experimental options. NB: Use at your own discretion. Are considered non-breaking if altered or removed!
        this.experimental = null;
        this.breakpointObserver = new ThemeBreakpointObserver(() => this.requestUpdate());
        // Defines the state for copy design code in the shopping list and add to bag sheets.
        this.copyDesignState = null;
        this.tracker = null;
        this.abTest = null;
        this.dexfSettings = null;
        this.numberOfCustomTabs = 0;
        this.customTabSlots = [];
        this.animatedPrice = false;
        this.listeners = {
            beforeprint: this.onBeforePrint.bind(this),
            afterprint: this.onAfterPrint.bind(this)
        };
        this.printReturnTo = null;
        this.customPrintAssignedNodes = [];
    }
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        const direction = SafariWriteDirectionUtility.getWriteDirection(this);
        const breakpointIndex = this.breakpointObserver.getBreakpointIndex() ?? ThemeBreakpointIndexEnum.small;
        const details = html `<div class="details">
			${this.isHeroImageCarouselExperiment()
            ? this.getHeroImageCarousel()
            : null}${this.getDetailsTitle()}${this.getTabs()}
		</div>`;
        const headingArea = html `
			<div class="heading-area">
				${this.getTitle()} ${this.getEditDesignButton(breakpointIndex)} ${this.getPrintButton()}
			</div>
		`;
        const overview = html `
			<section class="overview" aria-labelledby="overview-title">
				${this.getOverviewTitle()} ${this.isHeroImageCarouselExperiment()
            ? this.getHeroImageCarousel(true)
            : this.getScreenshot()}
				<div class="overview-sticky">
					${this.getConfigurationSummary()} ${this.getInlineMessages()} ${this.getAssistanceCard()}
					${this.getAssemblyServices()} ${this.getQrCode()}
				</div>
			</section>
		</div>`;
        const content = breakpointIndex < ThemeBreakpointIndexEnum.large &&
            !SummaryPageBrowserUtility.isSafari14OrOlder()
            ? html `${headingArea} ${overview} ${details}`
            : html `${headingArea} ${details} ${overview}`;
        this.setDelayedAnimatedPrice();
        return html `
			<style>
				${SummaryPageStyle.getStyle({
            theme: this.theme,
            isOldSafari: SummaryPageBrowserUtility.isSafari14OrOlder(),
            enableMargins: this.enableMargins,
            enableMainMargins: this.enableMainMargins,
            direction,
            includesScreenshot: !!this.screenshotUrl ||
                (this.isHeroImageCarouselExperiment() && (this.images?.length ?? 0) > 0)
        })}
			</style>
			<div class="wrapper">
				<div class="container">
					<div class="grid">${this.getSummaryPagePrint()} ${content}</div>
				</div>
			</div>
			${this.getZipIn()}
		`;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.breakpointObserver.disconnect();
        this.disconnectTracker();
        window.removeEventListener('beforeprint', this.listeners.beforeprint);
        window.removeEventListener('afterprint', this.listeners.afterprint);
    }
    async connectedCallback() {
        this.customElements.define('kompis-assembly-services', AssemblyServices);
        this.customElements.define('kompis-assistance-card', AssistanceCard);
        this.customElements.define('kompis-configuration-summary', ConfigurationSummary);
        this.customElements.define('kompis-image', Image);
        this.customElements.define('kompis-qr-code', QrCode);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-summary-page-images', SummaryPageImages);
        this.customElements.define('kompis-media-carousel', MediaCarousel);
        this.customElements.define('kompis-summary-page-product-list', SummaryPageProductList);
        this.customElements.define('kompis-summary-page-security', SummaryPageSecurity);
        this.customElements.define('kompis-summary-page-tabs', SummaryPageTabs);
        this.customElements.define('kompis-summary-page-print', SummaryPagePrint);
        this.customElements.define('kompis-zip-in', ZipIn);
        registerIcon(checkmarkCircle);
        registerIcon(incorrect);
        registerIcon(informationCircle);
        registerIcon(pencil);
        registerIcon(warningTriangle);
        registerIcon(printer);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('skapa-inline-message', SkapaInlineMessage);
        this.customElements.define('skapa-tabs', SkapaTabs);
        super.connectedCallback();
        window.addEventListener('beforeprint', this.listeners.beforeprint);
        window.addEventListener('afterprint', this.listeners.afterprint);
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (changedProperties.has('theme')) {
            this.breakpointObserver.observe(this.theme);
        }
        if (changedProperties.has('analytics') || changedProperties.has('settings')) {
            this.connectTracker();
        }
        if (changedProperties.has('customTabButtons')) {
            this.numberOfCustomTabs = SummaryPageCustomTabsUtility.getNumberOfCustomTabsToShow(this.customTabButtons);
            this.customTabSlots = SummaryPageCustomTabsUtility.getCustomTabSlots(this.numberOfCustomTabs);
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation;
    }
    getTranslations() {
        return this.translations
            ? this.translations
            : this.localizedInformation?.translations;
    }
    getSettings() {
        return this.dexfSettings;
    }
    isHeroImageCarouselExperiment() {
        return !!this.experimental?.heroImageCarousel;
    }
    onBeforePrint() {
        if (!this.printHoistTarget) {
            return;
        }
        const self = this;
        this.printReturnTo = self.parentNode;
        /*
            When using the SummaryPage via the SummaryPageController there is an issue when hoisting the SummaryPage containing custom print content.
            The slotted content won't follow the hoisted SummaryPage unless it is a child of the SummaryPage.
            Therefore we want to move the slot content to into the slot in SummaryPage.
        */
        const customPrintContentSlot = this.querySelector('slot[name="custom-print-content"]');
        if (customPrintContentSlot) {
            this.customPrintAssignedNodes = customPrintContentSlot
                .assignedNodes({ flatten: true })
                .map((node) => {
                return {
                    node,
                    prevParentNode: node.parentNode
                };
            });
            customPrintContentSlot.innerHTML = '';
            this.customPrintAssignedNodes.forEach((assignedNode) => {
                customPrintContentSlot.appendChild(assignedNode.node);
            });
        }
        this.printHoistTarget.prepend(self);
    }
    onAfterPrint() {
        const self = this;
        this.printReturnTo?.prepend(self);
        this.printReturnTo = null;
        // Restore the slot content to the original parent node
        const customPrintContentSlot = this.querySelector('slot[name="custom-print-content"]');
        if (customPrintContentSlot && this.customPrintAssignedNodes.length !== 0) {
            this.customPrintAssignedNodes.forEach((assignedNode) => {
                if (assignedNode.prevParentNode) {
                    assignedNode.prevParentNode.appendChild(assignedNode.node);
                }
            });
            this.customPrintAssignedNodes = [];
        }
    }
    /**
     * This is made to work with planners on the S2 platform specifically.
     */
    isKiosk() {
        return UrlUtility.getQueryValue(window.parent.location.href, 'uiPlatform') === 'kiosk';
    }
    getLongTermId() {
        const localStorageLongTermId = localStorage.getItem('long_term_id');
        return localStorageLongTermId ? JSON.parse(localStorageLongTermId)?.id : null;
    }
    async connectTracker() {
        if (!this.analytics || !this.hasTranslationsAndSettings()) {
            return;
        }
        this.disconnectTracker();
        const isKiosk = this.isKiosk();
        const longTermId = this.getLongTermId();
        this.tracker = new SummaryPageTracker({
            ...this.analytics,
            settings: this.getSettings(),
            locale: this.locale,
            kioskLongTermId: isKiosk ? longTermId : null
        });
        this.abTest = this.analytics.__INTERNAL__overrideABTest ?? this.tracker.getABTest();
        await this.tracker?.connect();
        this.sendInitialConfigurationEvent();
        // Make sure child components with analytics connect their trackers when the id is available.
        this.requestUpdate();
    }
    disconnectTracker() {
        this.tracker?.disconnect();
        this.tracker = null;
    }
    /**
     * Turns the animation on with a delay to always be activated when doing changes during summary but not for the initial loading.
     */
    setDelayedAnimatedPrice() {
        if (this.animatedPrice) {
            return;
        }
        setTimeout(() => {
            this.animatedPrice = true;
            this.requestUpdate();
        }, 500);
    }
    getHeroImageCarousel(showScrollBar) {
        return html `<kompis-media-carousel
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.theme="${this.theme}"
			.mediaItems="${this.images}"
			.showScrollbar="${showScrollBar}"
			@change="${({ detail }) => {
            this.dispatchEvent(new CustomEvent('selectedImageIndexChange', {
                bubbles: true,
                detail: { selectedIndex: detail.selectedIndex }
            }));
        }}"
		></kompis-media-carousel>`;
    }
    getTitle() {
        const title = this.getTranslations().summaryPageTitle;
        return this.startHeadingsAtH2
            ? html `<h2 class="title text--heading-l">${title}</h2>`
            : html `<h1 class="title text--heading-l">${title}</h1>`;
    }
    getDetailsTitle() {
        const detailsTitle = this.getTranslations().summaryPageDetailsTitle;
        return this.startHeadingsAtH2
            ? html `<h3 class="visuallyHidden">${detailsTitle}</h3>`
            : html `<h2 class="visuallyHidden">${detailsTitle}</h2>`;
    }
    getOverviewTitle() {
        const overviewTitle = this.getTranslations().summaryPageOverviewTitle;
        return html `
			${this.startHeadingsAtH2
            ? html `<h3 id="overview-title" class="visuallyHidden">${overviewTitle}</h3>`
            : html `<h2 id="overview-title" class="visuallyHidden">${overviewTitle}</h2>`}
		`;
    }
    getPrintButton() {
        if (!this.showPrintButton) {
            return null;
        }
        return html `<skapa-icon-button
			class="hidden-print"
			variant="tertiary"
			size="small"
			@click="${(event) => {
            event.preventDefault();
            event.stopPropagation();
            this.dispatchEvent(new CustomEvent('printButtonClick', {
                bubbles: true
            }));
            this.tracker?.sendPrintButtonClick();
        }}"
			><skapa-icon icon="printer" slot="icon"></skapa-icon>${this.getTranslations()
            .print}</skapa-icon-button
		>`;
    }
    getEditDesignButton(breakpointIndex) {
        if (!this.showEditDesignButton) {
            return null;
        }
        const buttonText = this.getTranslations().navMenuNavigationButtonSummary;
        if (breakpointIndex <= ThemeBreakpointIndexEnum.small) {
            return html `<skapa-icon-button
				class="hidden-print"
				variant="tertiary"
				size="small"
				@click="${this.onEditDesignButtonClick.bind(this)}"
				>${buttonText}<skapa-icon slot="icon" icon="pencil"></skapa-icon
			></skapa-icon-button>`;
        }
        return html `<skapa-button
			class="hidden-print"
			variant="tertiary"
			size="small"
			@click="${this.onEditDesignButtonClick.bind(this)}"
			><skapa-icon slot="icon" icon="pencil"></skapa-icon>${buttonText}</skapa-button
		>`;
    }
    onEditDesignButtonClick() {
        this.dispatchEvent(new CustomEvent('editDesignButtonClick', { bubbles: true }));
        this.tracker?.sendEditDesignButtonClick();
    }
    /**
     * Get inline messages. Limits to three to prevent too many messages.
     *
     * @returns Inline messages.
     */
    getInlineMessages() {
        const firstThreeMessages = this.messageSection?.slice(0, 3);
        return html `${firstThreeMessages?.map(({ title, message, icon = InlineMessageIconEnum.information, variant = InlineMessageLayoutEnum.informative }) => {
            // Check so that our enum matches Skapa type
            const inlineMessageVariant = variant;
            return html `<skapa-inline-message class="message" variant="${inlineMessageVariant}">
					<skapa-icon slot="message-icon" icon="${icon}"></skapa-icon>
					<h3 slot="title">${title}</h3>
					<span slot="body">${message}</span>
				</skapa-inline-message>`;
        })}`;
    }
    sendInitialConfigurationEvent() {
        const tabs = [SummaryPageTabEnum.productList];
        if (this.showImagesTab()) {
            tabs.push(SummaryPageTabEnum.images);
        }
        if (this.showSecurityTab()) {
            tabs.push(SummaryPageTabEnum.security);
        }
        for (let n = 1; n <= this.numberOfCustomTabs; n++) {
            tabs.push(SummaryPageTabEnum[`customTab${n}`]);
        }
        this.tracker?.sendInitialConfiguration({
            enabledTabs: tabs,
            qrCode: !!this.designLink,
            screenshot: !!this.screenshotUrl,
            measurements: !!this.measurements,
            productList: {
                designText: this.showDesignText,
                productMeasures: this.productMeasures,
                collectable: this.collectableProducts
            }
        });
    }
    showImagesTab() {
        return !!this.images?.length;
    }
    showSecurityTab() {
        return !!this.securityInformation;
    }
    getTabs() {
        return html `<kompis-summary-page-tabs
			.theme="${this.theme}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.settings="${this.settings}"
			.shoppingProducts="${this.shoppingProducts}"
			.storeAvailabilities="${this.storeAvailabilities}"
			.stores="${this.stores}"
			.storeId="${this.storeId}"
			.productMeasures="${this.productMeasures}"
			.visibleModal="${this.visibleModal}"
			.disableLinks="${this.disableLinks}"
			.homeDelivery="${this.homeDelivery}"
			.includedProducts="${this.includedProducts}"
			.pickingListPrintLayout="${this.pickingListPrintLayout}"
			.collectableProducts="${this.collectableProducts}"
			.collectedProducts="${this.collectedProducts}"
			.showDesignText="${this.showDesignText}"
			.productListSections="${this.productListSections}"
			.productListAddable="${this.productListAddable}"
			.productListRemovable="${this.productListRemovable}"
			.defaultSortBy="${this.defaultSortBy}"
			.startHeadingsAtLevel="${this.startHeadingsAtH2 ? 4 : 3}"
			.images="${this.images}"
			.securityInformation="${this.securityInformation}"
			.customTabButtons="${this.customTabButtons}"
			.contentToPrint="${this.contentToPrint}"
			.zipCode="${this.zipCode}"
			.measurementSystem="${this.measurementSystem}"
			.productListLink="${this.productListLink}"
			.experimental="${this.experimental}"
			@modalOpen="${(event) => {
            this.dispatchEvent(new CustomEvent('modalOpen', { bubbles: true, detail: event.detail }));
        }}"
			@modalClose="${(event) => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: event.detail }))}"
			@storeSelectorChange="${({ detail }) => {
            this.dispatchEvent(new CustomEvent('storeSelectorChange', { bubbles: true, detail }));
            this.tracker?.sendStoreSelectorChange(detail.storeId);
        }}"
			@sortListChange="${({ detail }) => this.dispatchEvent(new CustomEvent('sortListChange', {
            bubbles: true,
            detail
        }))}"
			@homeDeliveryClick="${() => {
            this.dispatchEvent(new CustomEvent('openZipIn', {
                bubbles: true
            }));
            this.tracker?.sendHomeDeliveryClick();
        }}"
			@collectProduct="${(event) => {
            this.dispatchEvent(new CustomEvent('collectProduct', { bubbles: true, detail: event.detail }));
            this.tracker?.sendCollectProduct({ collectId: event.detail.collectId, collected: event.detail.collected }, this.vpcCode);
        }}"
			@productListAdd="${(event) => {
            this.dispatchEvent(new CustomEvent('productListAdd', { bubbles: true, detail: event.detail }));
        }}"
			@productListRemove="${(event) => {
            this.dispatchEvent(new CustomEvent('productListRemove', { bubbles: true, detail: event.detail }));
        }}"
			@productListExpand="${(event) => {
            this.dispatchEvent(new CustomEvent('productListExpand', { bubbles: true, detail: event.detail }));
        }}"
			@productListNameClick="${(event) => {
            this.dispatchEvent(new CustomEvent('productListNameClick', { bubbles: true, detail: event.detail }));
        }}"
			@selectedImageIndexChange="${({ detail }) => {
            this.dispatchEvent(new CustomEvent('selectedImageIndexChange', {
                bubbles: true,
                detail
            }));
            this.tracker?.sendSelectedImageIndexChange(detail.selectedIndex, this.images?.length);
        }}"
			@tabChange="${({ detail }) => {
            this.dispatchEvent(new CustomEvent('tabChange', {
                bubbles: true,
                detail
            }));
            // Temporary fix for the image carousel becoming unresponsive when changing tabs. This is a bug inside skapa and should be removed when fixed in their lib.
            // https://r-e-x.slack.com/archives/CSGP9GMED/p1745320387190469
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 50);
            this.tracker?.sendTabChange(detail.selectedTab);
        }}"
		>
			${this.customTabSlots}
			<slot slot="custom-print-content" name="custom-print-content"></slot>
		</kompis-summary-page-tabs>`;
    }
    getValidAndAddedShoppingProducts() {
        return this.shoppingProducts
            .flatMap((shoppingProduct) => this.flattenNonPIAShoppingProduct(shoppingProduct))
            .filter((shoppingProduct) => shoppingProduct.product.valid)
            .filter((shoppingProduct) => {
            if (!this.productListAddable) {
                return true;
            }
            return !this.productListAddable.includes(shoppingProduct.product.itemId);
        });
    }
    flattenNonPIAShoppingProduct(shoppingProduct) {
        if (ProductUtility.shouldCalculatePriceFromChildProducts(shoppingProduct.product)) {
            const includedShoppingProducts = ProductUtility.getIncludedShoppingProducts({
                product: shoppingProduct.product,
                includedProducts: this.includedProducts ?? [],
                ignoreInvalidProducts: true
            });
            if (includedShoppingProducts.length > 0 &&
                includedShoppingProducts.length === shoppingProduct.product.content?.child?.length) {
                return includedShoppingProducts.map((sp) => ({
                    ...sp,
                    quantity: sp.quantity * shoppingProduct.quantity
                }));
            }
        }
        return [shoppingProduct];
    }
    /**
     * Get Configuration Summary.
     *
     * @returns The Configuration Summary.
     */
    getConfigurationSummary() {
        const shareDesignCard = this.shareDesignCard
            ? {
                ...this.shareDesignCard,
                designLink: this.designLink
            }
            : null;
        return html `<kompis-configuration-summary
			.theme="${this.theme}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.settings="${this.settings}"
			.shoppingProducts="${this.getValidAndAddedShoppingProducts()}"
			.storeAvailabilities="${this.storeAvailabilities}"
			.productName="${this.productName}"
			.showAddToBag="${this.showAddToBag}"
			.showShareDesign="${this.showShareDesign}"
			.showSaveDesign="${this.showSaveDesign}"
			.saveDesignToProfile="${this.saveDesignToProfile}"
			.logInAndSaveState="${this.logInAndSaveState}"
			.addToCartState="${this.addToCartState}"
			.addToListState="${this.addToListState}"
			.shareDesignState="${this.shareDesignState}"
			.kioskMainCTAButtonState="${this.kioskMainCTAButtonState}"
			.includedProducts="${this.includedProducts}"
			.confirmationCardType="${this.confirmationCardType}"
			.copyDesignState="${this.copyDesignState}"
			.visibleModal="${this.visibleModal?.itemId === null ? this.visibleModal.modal : null}"
			.vpcCode="${this.vpcCode}"
			.unavailableOnlineBehaviour="${UnavailableOnlineBehaviourEnum.continueByShoppingList}"
			.addUnavailableProductsToList="${this.addUnavailableProductsToList}"
			.failedShoppingItems="${this.failedShoppingItems}"
			.disableLinks="${this.disableLinks}"
			.screenshotUrl="${this.screenshotUrl}"
			.screenshotAlt="${this.screenshotAlt}"
			.confirmationCardState="${this.confirmationCardState}"
			.financingOption="${this.financingOption}"
			.showTotalPackageWeight="${this.showTotalPackageWeight}"
			.locale="${this.locale}"
			.analytics="${this.getAnalyticsForSubComponents()}"
			.showKioskMainCTAButton="${this.showKioskMainCTAButtonWithABVersion()}"
			.linkCard="${this.linkCard}"
			.shareDesignCard="${shareDesignCard}"
			.buyInStore="${this.getBuyInStoreOptions()}"
			.productDescription="${this.productDescription}"
			.animatedPrice="${this.animatedPrice}"
			.measurementSystem="${this.measurementSystem}"
			@shareDesign="${({ detail }) => {
            const { triggerElement, ...restOfDetail } = detail;
            this.dispatchEvent(new CustomEvent('shareDesign', { bubbles: true, detail: restOfDetail }));
        }}"
			@saveDesign="${() => {
            this.dispatchEvent(new CustomEvent('saveDesign', { bubbles: true }));
        }}"
			@logInAndSaveDesign="${({ detail }) => {
            this.dispatchEvent(new CustomEvent('logInAndSaveDesign', { bubbles: true, detail }));
        }}"
			@addToCart="${({ detail }) => {
            this.dispatchEvent(new CustomEvent('addToCart', { bubbles: true, detail: { ...detail } }));
            this.tracker?.sendAddToCartAvailability(this.homeDelivery?.zipAvailabilities ?? null, this.storeAvailabilities);
        }}"
			@addToList="${({ detail }) => {
            const { triggerElement, ...restOfDetail } = detail;
            this.dispatchEvent(new CustomEvent('addToList', { bubbles: true, detail: restOfDetail }));
            this.tracker?.sendAddToListAvailability(this.homeDelivery?.zipAvailabilities ?? null, this.storeAvailabilities);
        }}"
			@resetAddToCartState="${() => this.dispatchEvent(new CustomEvent('resetAddToCartState', { bubbles: true }))}"
			@resetAddToListState="${() => this.dispatchEvent(new CustomEvent('resetAddToListState', { bubbles: true }))}"
			@addToCartClick="${() => this.dispatchEvent(new CustomEvent('addToCartClick', {
            bubbles: true
        }))}}"
			@unavailableProductsModalContinue="${() => this.dispatchEvent(new CustomEvent('unavailableProductsModalContinue', {
            bubbles: true
        }))}}"
			@modalOpen="${(event) => {
            this.dispatchEvent(new CustomEvent('modalOpen', {
                bubbles: true,
                detail: {
                    visibleModal: { modal: event.detail.visibleModal, itemId: null }
                }
            }));
        }}"
			@modalClose="${({ detail }) => {
            const { triggerElement, ...restOfDetail } = detail;
            this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: restOfDetail }));
            triggerElement?.focus();
        }}"
			@copyDesign="${(event) => {
            this.copyDesignState = event.detail.copyDesignState;
            this.dispatchEvent(new CustomEvent('copyDesign', { bubbles: true, detail: event.detail }));
            this.requestUpdate();
        }}"
			@addUnavailableProductsToListChange="${(event) => this.dispatchEvent(new CustomEvent('addUnavailableProductsToListChange', {
            bubbles: true,
            detail: event.detail
        }))}"
			@addUnavailableToCartAndList="${(event) => this.dispatchEvent(new CustomEvent('addUnavailableToCartAndList', { bubbles: true, detail: event.detail }))}"
			@confirmationCardStateChange="${(event) => this.dispatchEvent(new CustomEvent('confirmationCardStateChange', { bubbles: true, detail: event.detail }))}"
			@financialServicesContinueButtonClick="${({ detail }) => this.dispatchEvent(new CustomEvent('financialServicesContinueButtonClick', { bubbles: true, detail }))}"
			@financialServicesMoreInfoClick="${({ detail }) => {
            this.dispatchEvent(new CustomEvent('financialServicesMoreInfoClick', { bubbles: true, detail }));
        }}"
			@kioskMainCtaButtonClick="${(event) => this.dispatchEvent(new CustomEvent('kioskMainCtaButtonClick', { bubbles: true, detail: event.detail }))}"
			@linkCardClick="${(event) => this.dispatchEvent(new CustomEvent('linkCardClick', { bubbles: true, detail: event.detail }))}"
			@shareDesignCardStateChange="${({ detail }) => this.dispatchEvent(new CustomEvent('shareDesignCardStateChange', { bubbles: true, detail }))}"
			@sendByEmailInput="${({ detail }) => this.dispatchEvent(new CustomEvent('sendByEmailInput', { bubbles: true, detail }))}"
			@sendByEmailValidation="${({ detail }) => this.dispatchEvent(new CustomEvent('sendByEmailValidation', { bubbles: true, detail }))}"
			@sendByEmailSend="${({ detail }) => this.dispatchEvent(new CustomEvent('sendByEmailSend', { bubbles: true, detail }))}"
			@sendByEmailReset="${({ detail }) => this.dispatchEvent(new CustomEvent('sendByEmailReset', { bubbles: true, detail }))}"
			@copyDesignCodeClick="${({ detail }) => this.dispatchEvent(new CustomEvent('copyDesignCodeClick', { bubbles: true, detail }))}"
			@copyDesignCodeReset="${() => this.dispatchEvent(new CustomEvent('copyDesignCodeReset', { bubbles: true }))}"
			@copyDesignLinkClick="${({ detail }) => this.dispatchEvent(new CustomEvent('copyDesignLinkClick', { bubbles: true, detail }))}"
			@copyDesignLinkReset="${() => this.dispatchEvent(new CustomEvent('copyDesignLinkReset', { bubbles: true }))}"
		>
			${this.getMeasurements()}
		</kompis-configuration-summary>`;
    }
    /**
     * Get Measurements.
     * Short or long format depending on prop. Unit is cm/in depending on localizedInformation.
     */
    getMeasurements() {
        if (!this.measurements) {
            return null;
        }
        const useLongFormat = this.configurationMeasures === SummaryPageConfigurationMeasureEnum.long;
        const measurementRows = SummaryPageMeasurementUtility.getLocalizedMeasurementRows(this.getTranslations(), this.getSettings(), this.locale, this.measurements, useLongFormat, this.measurementSystem);
        return html `<span class="measurements">
			${measurementRows.map((row) => html `${row}<br />`)}
		</span>`;
    }
    getScreenshot() {
        return this.screenshotUrl
            ? html `<img
					class="screenshot"
					alt="${this.screenshotAlt ?? this.getTranslations().summaryPageScreenshotAltText}"
					src="${this.screenshotUrl}"
				/>`
            : null;
    }
    getAssistanceCard() {
        if (!this.showAssistanceCard) {
            return null;
        }
        return html `<kompis-assistance-card
			.theme="${this.theme}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.settings="${this.settings}"
			.vpcCode="${this.vpcCode}"
			.applicationName="${this.assistanceCardApplicationName}"
			.disableLinks="${this.disableLinks}"
			@linkClick="${() => {
            this.tracker?.sendBookAssistanceLinkClick();
            this.dispatchEvent(new CustomEvent('assistanceCardLinkClick', {
                bubbles: true
            }));
        }}"
		></kompis-assistance-card>`;
    }
    getAssemblyServices() {
        if (!this.showAssemblyServices) {
            return null;
        }
        return html `<kompis-assembly-services
			.theme="${this.theme}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.settings="${this.settings}"
			.assemblyServicesData="${this.assemblyServices?.assemblyServicesData}"
			.zipCode="${this.zipCode}"
			.disableLinks="${this.disableLinks}"
			.analytics="${this.getAnalyticsForSubComponents()}"
			@assemblyServicesClick="${() => {
            this.dispatchEvent(new CustomEvent('openZipIn', {
                bubbles: true
            }));
        }}"
		></kompis-assembly-services>`;
    }
    getBuyInStoreOptions() {
        const kioskExitpointTestVersion = this.kioskExitpointTestVersion();
        if (kioskExitpointTestVersion === ABTestVariationEnum.A) {
            return null;
        }
        if (kioskExitpointTestVersion === ABTestVariationEnum.B) {
            const url = this.getQRUrl();
            if (!url) {
                return null;
            }
            return {
                buyInStoreLink: url
            };
        }
        return this.buyInStore;
    }
    showKioskMainCTAButtonWithABVersion() {
        const kioskExitpointTestVersion = this.kioskExitpointTestVersion();
        if (kioskExitpointTestVersion === ABTestVariationEnum.A) {
            return false;
        }
        if (kioskExitpointTestVersion === ABTestVariationEnum.B) {
            return !!this.getQRUrl();
        }
        return this.showKioskMainCTAButton;
    }
    kioskExitpointTestVersion() {
        if (!this.abTest) {
            return null;
        }
        const { name, variation } = this.abTest;
        if (!this.isKiosk() || name !== ABTestNameEnum.qrExitpointTest) {
            return null;
        }
        return variation;
    }
    getQRUrl() {
        const url = UrlUtility.addQueryParameter(this.designLink, 'vpcSource', DesignSourceEnum.qr);
        if (this.abTest?.name === ABTestNameEnum.qrExitpointTest && this.isKiosk()) {
            return UrlUtility.addQueryParameter(url, this.abTest.name, this.abTest.variation);
        }
        return url;
    }
    /**
     * Get QR Code for the first page.
     *
     * @returns The QR code.
     */
    getQrCode() {
        const url = this.getQRUrl();
        if (!url || this.kioskExitpointTestVersion() === ABTestVariationEnum.B) {
            return null;
        }
        return html `<div class="qr-code">
			<kompis-qr-code
				.url="${url}"
				.qrCodeTitle="${this.getTranslations().summaryPageQrCodeTitle}"
			></kompis-qr-code>
			<p class="qr-code-text text--body-m">${this.getTranslations().summaryPageQrCodeText}</p>
		</div>`;
    }
    getSummaryPagePrint() {
        const firstImage = Array.isArray(this.images) ? this.images[0] : null;
        const firstImageUrl = firstImage?.src ?? null;
        const firstImageAlt = firstImage?.alt ?? null;
        return html `
			<kompis-summary-page-print
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.locale="${this.locale}"
				.shoppingProducts="${this.getValidAndAddedShoppingProducts()}"
				.configurationMeasures="${this.configurationMeasures}"
				.screenshotUrl="${this.isHeroImageCarouselExperiment()
            ? firstImageUrl
            : this.screenshotUrl}"
				.screenshotAlt="${this.isHeroImageCarouselExperiment()
            ? firstImageAlt
            : this.screenshotAlt}"
				.productName="${this.productName}"
				.vpcCode="${this.vpcCode}"
				.measurements="${this.measurements}"
				.designLink="${this.designLink}"
				.showTotalPackageWeight="${this.showTotalPackageWeight}"
				.includedProducts="${this.includedProducts}"
				.storeName="${this.getStoreName()}"
				.retailUnit="${this.getRetailUnit()}"
				.measurementSystem="${this.measurementSystem}"
				@qrCodeRendered="${() => this.dispatchEvent(new CustomEvent('printQrCodeRendered', { bubbles: true }))}"
				;
			></kompis-summary-page-print>
		`;
    }
    getZipIn() {
        const visibleModal = this.visibleModal?.modal === SummaryPageModalEnum.zipIn ? ZipInModalEnum.zipIn : null;
        return html `
			<kompis-zip-in
				.theme="${this.theme}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.visibleModal="${visibleModal}"
				.zipCode="${this.zipCode}"
				.state="${this.zipInCardState}"
				.analytics="${this.getAnalyticsForSubComponents()}"
				@modalOpen="${(event) => {
            this.dispatchEvent(new CustomEvent('modalOpen', {
                bubbles: true,
                detail: {
                    visibleModal: {
                        itemId: null,
                        modal: event.detail.visibleModal
                    }
                }
            }));
        }}"
				@modalClose="${(event) => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: event.detail }))}"
				@save="${(event) => this.dispatchEvent(new CustomEvent('saveZipCode', { bubbles: true, detail: event.detail }))}"
				@forget="${(event) => this.dispatchEvent(new CustomEvent('forgetZipCode', { bubbles: true, detail: event.detail }))}"
			></kompis-zip-in>
		`;
    }
    getStoreName() {
        if (!this.storeId || !this.stores) {
            return null;
        }
        const store = this.stores.find((store) => store.id === this.storeId);
        return store?.name ?? null;
    }
    getRetailUnit() {
        return this.locale.split('-').pop() ?? null;
    }
    getAnalyticsForSubComponents() {
        if (!this.analytics) {
            return null;
        }
        const reporterId = this.tracker?.getConnectedReporterId();
        return {
            ...this.analytics,
            context: this.analytics.context ?? SummaryPageAnalyticsContext,
            locale: this.getLocaleForAnalyticsOrThrow(),
            __INTERNAL__reporter: reporterId ? { connected: true, id: reporterId } : { connected: false }
        };
    }
    /**
     * Get the locale or throw error if locale is null.
     *
     * @returns The locale.
     */
    getLocaleForAnalyticsOrThrow() {
        if (!this.locale) {
            throw Error("The property 'locale' must be set when analytics is enabled.");
        }
        return this.locale;
    }
}
__decorate([
    property({ type: Object, required: true })
], SummaryPage.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "settings", void 0);
__decorate([
    property({ type: String, required: true })
], SummaryPage.prototype, "locale", void 0);
__decorate([
    property({ type: Array, required: true })
], SummaryPage.prototype, "shoppingProducts", void 0);
__decorate([
    property({ type: Array })
], SummaryPage.prototype, "storeAvailabilities", void 0);
__decorate([
    property({ type: Array })
], SummaryPage.prototype, "stores", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "storeId", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "productMeasures", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "configurationMeasures", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "screenshotUrl", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "screenshotAlt", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "productName", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "productDescription", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "showShareDesign", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "showSaveDesign", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "showAddToBag", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "vpcCode", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "disableLinks", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "visibleModal", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "addToCartState", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "addToListState", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "shareDesignState", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "kioskMainCTAButtonState", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "confirmationCardState", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "confirmationCardType", void 0);
__decorate([
    property({ type: Array })
], SummaryPage.prototype, "images", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "securityInformation", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "measurements", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "addUnavailableProductsToList", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "designLink", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "shareDesignCard", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "buyInStore", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "saveDesignToProfile", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "logInAndSaveState", void 0);
__decorate([
    property({ type: Object, synchronous: true })
], SummaryPage.prototype, "homeDelivery", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "assemblyServices", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "zipCode", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "zipInCardState", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "collectableProducts", void 0);
__decorate([
    property({ type: Array })
], SummaryPage.prototype, "collectedProducts", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "showDesignText", void 0);
__decorate([
    property({ type: Array })
], SummaryPage.prototype, "productListSections", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "customTabButtons", void 0);
__decorate([
    property({ type: Array })
], SummaryPage.prototype, "failedShoppingItems", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "startHeadingsAtH2", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "financingOption", void 0);
__decorate([
    property({ type: Boolean })
    /**
     * @deprecated Use the Main Header and Main Footer components together with the enableMainMargins prop instead.
     */
], SummaryPage.prototype, "enableMargins", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "enableMainMargins", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "showTotalPackageWeight", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "showAssistanceCard", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "showAssemblyServices", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "assistanceCardApplicationName", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "showKioskMainCTAButton", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "showPrintButton", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPage.prototype, "showEditDesignButton", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "analytics", void 0);
__decorate([
    property({ type: Array })
], SummaryPage.prototype, "includedProducts", void 0);
__decorate([
    property({ type: Array })
], SummaryPage.prototype, "productListAddable", void 0);
__decorate([
    property({ type: Array })
], SummaryPage.prototype, "productListRemovable", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "defaultSortBy", void 0);
__decorate([
    property({ type: Array })
], SummaryPage.prototype, "messageSection", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "linkCard", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "contentToPrint", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "pickingListPrintLayout", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "measurementSystem", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "printHoistTarget", void 0);
__decorate([
    property({ type: String })
], SummaryPage.prototype, "productListLink", void 0);
__decorate([
    property({ type: Object })
], SummaryPage.prototype, "experimental", void 0);
//# sourceMappingURL=SummaryPage.js.map